import { useEffect } from 'react';
import { useLocalForageContext, useAuthContext } from '../context';
import { useApolloClient } from '@apollo/react-hooks';
import moment from 'moment';
import localforage from 'localforage';
import {
  getOpenItemsQuery,
  getOpenItemsQueryOptions,
} from '@msales/shared/graphql/queries/openItems/getOpenItems';
import { useOfflineModeContext } from '../context/offlineContext';
import { useInsertEventLog } from './useInsertEventLog';
import { notificationManager } from '../utils/notificationManager';
import { reportSyncError } from '../utils/sentryHelpers';

export const useOpenItemsOfflineData = () => {
  const {
    loadingOpenItems: loading,
    setLoadingOpenItems: setLoading,
    refetchOpenItems: refetch,
    setRefetchOpenItems: setRefetch,
  } = useLocalForageContext();

  const { authUser } = useAuthContext();
  const { insertEvent } = useInsertEventLog();

  const openItemsStore = localforage.createInstance({
    name: 'OpenItems',
  });

  const { networkState } = useOfflineModeContext();
  const apolloClient = useApolloClient();

  useEffect(() => {
    const FetchProcess = async () => {
      const shouldAsync = async (refetch) => {
        try {
          const createdAt = await openItemsStore.getItem('createdAt');
          if (
            (refetch ||
              !createdAt ||
              (false &&
                moment().diff(
                  moment(createdAt, 'DD/MM/YYYY HH:mm:ss'),
                  'minutes',
                ) >= 20)) &&
            (!networkState || networkState.online) &&
            authUser &&
            !loading
          ) {
            if (refetch) setRefetch(false);
            return true;
          }
          if (refetch) setRefetch(false);
          return false;
        } catch (err) {
          console.error('Error in shouldAsync:', err);
          return false;
        }
      };

      const AsyncOpenItems = async () => {
        try {
          setLoading(true);
          notificationManager.startSync('openItems', 'Otvorene stavke');

          try {
            const { data, error } = await apolloClient.query({
              ...getOpenItemsQueryOptions(),
              query: getOpenItemsQuery,
            });

            if (error) {
              throw new Error(error.message);
            }

            await openItemsStore.setItem('nodes', data?.openItems || []);
            await openItemsStore.setItem(
              'createdAt',
              moment().format('DD/MM/YYYY HH:mm:ss'),
            );

            insertEvent({
              eventCode: 'SYNC',
              remark: 'Otvorene stavke',
            });

            notificationManager.completeSync('openItems', true);
          } catch (err) {
            console.error('Error in AsyncOpenItems:', err);
            reportSyncError(err, 'openItems', networkState);

            let errorMessage = 'Greška prilikom učitavanja otvorenih stavki!';
            if (err.networkError?.statusCode === 400) {
              errorMessage = 'Neispravni podaci za učitavanje otvorenih stavki';
            } else if (err.networkError?.statusCode >= 500) {
              errorMessage =
                'Greška na serveru prilikom učitavanja otvorenih stavki';
            } else if (err.message?.includes('Firebase ID token')) {
              errorMessage = 'Greška autentifikacije';
            }

            notificationManager.completeSync('openItems', false, errorMessage);
          }
        } finally {
          setLoading(false);
        }
      };

      const callback = async (refetch) => {
        try {
          const should = await shouldAsync(refetch);
          if (should) {
            await AsyncOpenItems();
          }
        } catch (err) {
          console.error('Error in callback:', err);
        }
      };

      try {
        await callback(refetch);
      } catch (err) {
        console.error('Error in FetchProcess:', err);
      }
    };

    FetchProcess();

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, authUser]);

  return null;
};
